import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { Link } from "@tanstack/react-router";

import { CtaBand, PageHero } from "../components/site/Page";
import { Portrait } from "../components/site/Portrait";
import { staffListFrom } from "@/lib/staff";
import { staffPortraitsQuery, useStaffPortraits } from "@/lib/staff-portraits";
import {
  bioParagraphs,
  displayGroup,
  displayName,
  displayRole,
  profileFacts,
  staffProfile,
  staffProfilesQuery,
  useStaffProfiles,
  type StaffProfileMap,
} from "@/lib/staff-profiles";
import maria from "../assets/staff/portraits/maria.webp";
import beatriz from "../assets/staff/portraits/beatriz.webp";
import bojan from "../assets/staff/portraits/bojan.webp";
import anna from "../assets/staff/portraits/anna.webp";
import blanca from "../assets/staff/portraits/blanca.webp";
import malin from "../assets/staff/portraits/malin.webp";
import svetlana from "../assets/staff/portraits/svetlana.webp";
import farhad from "../assets/staff/portraits/farhad.webp";
import bojan2 from "../assets/staff/portraits/bojan2.webp";
import erik from "../assets/staff/portraits/erik.webp";
import { fillYearsFromProfiles } from "@/content/staff-facts";


const title = "Personal och lärare | Swedish International School";
const description =
  "Möt personalen på Swedish International School Estepona – Marbella: rektor, lärare, förskollärare och handledare med svensk lärarlegitimation.";

export const Route = createFileRoute("/personal")({
  head: ({ loaderData }) => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: absoluteUrl("/personal") },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/personal"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": staffListFrom(
            (loaderData as { profiles?: StaffProfileMap } | undefined)?.profiles ?? {},
          )
            .filter((p) => p.group !== "Grundare och styrelse")
            .map((p) => {
            const profile = staffProfile(
              (loaderData as { profiles?: StaffProfileMap } | undefined)?.profiles ?? {},
              p.key,
            );
            return {
              "@type": "Person",
              name: displayName(profile, p.name),
              jobTitle: displayRole(profile, p.role),
              worksFor: {
                "@type": "EducationalOrganization",
                name: "Swedish International School",
                url: absoluteUrl("/"),
              },
              url: absoluteUrl("/personal"),
              ...(profile.education ? { alumniOf: profile.education } : {}),
              ...(profile.credential ? { hasCredential: profile.credential } : {}),
              ...(profile.languages
                ? { knowsLanguage: profile.languages.split(/[,/]/).map((l) => l.trim()).filter(Boolean) }
                : {}),
            };
          }),
        }),
      },
    ],
  }),
  loader: async ({ context }) => {
    const qc = (context as any).queryClient;
    const [, profiles] = await Promise.all([
      qc?.ensureQueryData(staffPortraitsQuery),
      qc?.ensureQueryData(staffProfilesQuery),
    ]);
    return { profiles: (profiles?.profiles ?? {}) as StaffProfileMap };
  },
  component: Personal,
});

const staff = [
  {
    name: "Maria",
    role: "Rektor",
    key: "maria",
    image: maria,
    paragraphs: [
      "Jag är utbildad lärare och specialpedagog som de senaste {years:maria} åren arbetat som rektor på olika skolor i Helsingborg. Mitt bästa verktyg är att skapa goda, professionella relationer till mina elever, medarbetare och vårdnadshavare – något som skapar ett positivt gruppklimat, där måluppfyllelse och samarbete är självklara ledord. Detta gör jag med glädje och med mina ledord relation, motivation och kommunikation som ryggraden i mitt ledarskap.",
      "Jag rensar gärna tankarna i löpspåret eller bara genom att tillbringa tid på vår segelbåt.",
    ],
  },
  {
    name: "Beatriz",
    role: "Directora técnica",
    key: "beatriz",
    image: beatriz,
    paragraphs: [
      "Beatriz har jobbat som förskollärare och grundskollärare i {years:beatriz} år, och på Swedish International School sedan vårterminen 2025. Beatriz har ett genuint intresse för läsning, sport och natur och är även utbildad yogalärare.",
      "”I min roll som lärare strävar jag efter att ge mina elever verktyg så de kan utvecklas både kunskapsmässigt och socialt. Jag vill citera Alexander K. Trenfor för att definiera hur jag ser på min roll som lärare: ’Los mejores maestros son los que te dicen dónde mirar, pero no te dicen qué ver’ / ’De bästa lärarna är de som säger var man ska titta men som inte säger vad man ska se’.”",
    ],
  },
  {
    name: "Bojan",
    role: "Idrottslärare och fritidspedagog",
    key: "bojan",
    image: bojan,
    paragraphs: [
      "Bojan har jobbat {years:bojan} år som idrottslärare och fritidspedagog, och på Swedish International School sedan hösten 2023. Med erfarenhet både som idrottslärare, fritidspedagog, massör, kostrådgivare och vaktmästare har Bojan även hunnit med att spela basket på elitnivå i Sverige och utomlands.",
      "”För mig är det viktigt att skapa en miljö som främjar elevernas fysiska och mentala hälsa. I mitt uppdrag som lärare strävar jag efter att bygga tillitsfulla relationer med mina elever och uppmuntra dem så de kan nå så långt som möjligt i deras kunskapsutveckling.”",
      "Bojan är även handledare i idrott och hälsa för eleverna som läser årskurs 7–9 samt gymnasiet, och ansvarar för MFF-träningarna tillsammans med Malmö FF.",
    ],
  },
  {
    name: "Anna",
    role: "Ansvarig för förskolan",
    key: "anna",
    image: anna,
    paragraphs: [
      "Anna har {years:anna} års erfarenhet av att arbeta med barn i förskola och skola.",
      "”Jag startade en förskola i Orsa, Sverige och har sedan dess ansvarat för dess pedagogiska verksamhet och affärsutveckling. Att äga mitt eget företag har gett mig insikten att varje barn är unikt och har sina fantastiska förmågor och styrkor. Min roll som förskollärare är att upptäcka dessa sidor hos barnen och bidra till att utveckla dem vidare. Min absolut största styrka som förskollärare är att få barnens tillit och förtroende, och med det kan vi tillsammans utveckla en relation i respekt.”",
    ],
  },
  {
    name: "Blanca",
    role: "Förskoleklass och förskola",
    key: "blanca",
    image: blanca,
    paragraphs: [
      "Blanca har mer än {years:blanca} års erfarenhet av att undervisa främst mellanstadie- och gymnasiebarn, och har även erfarenhet av barn från förskola till grundskola.",
      "”För tillfället arbetar jag i förskolan på Swedish International School och lär ut spanska för de små barnen. Det viktigaste för mig är att mina elever förstår och lär sig de ämnen jag undervisar i, att de i framtiden kan fortsätta sina studier och ha en bra grund, både i kunskap och studieteknik.”",
    ],
  },
  {
    name: "Malin",
    role: "Lågstadielärare årskurs F–3",
    key: "malin",
    image: malin,
    paragraphs: [
      "Malin har arbetat inom skolan i totalt {years:malin} år och undervisar sedan hösten 2024 på Swedish International School som lågstadielärare för årskurs F–3. Malin ansvarar även för teknik, bild och GOTD i årskurs 4–6.",
      "”För mig är skolan en plats där magi händer. Mitt mål är att skapa en trygg och inspirerande miljö där elever vågar utvecklas och når sin fulla potential. Att få följa varje elevs resa är ett privilegium, och jag brinner för att göra skillnad varje dag.”",
    ],
  },
  {
    name: "Svetlana",
    role: "Klasslärare årskurs 4–6",
    key: "svetlana",
    image: svetlana,
    paragraphs: [
      "Svetlana har arbetat som grundskollärare och gymnasielärare i {years:svetlana} år, och på Swedish International School sedan höstterminen 2023.",
      "”För mig är det viktigt att skapa en trygg, tillgänglig och kreativ lärmiljö för alla mina elever, att följa deras utveckling och utvecklas tillsammans med dem. Jag brinner för språk och språkutvecklande arbetssätt. Spanska har alltid varit en viktig del i mitt liv, så att få arbeta här i den här miljön känns som att jag har fått komma hem.”",
      "Svetlana ansvarar även för undervisningen i spanska i åk 1–3 och är handledare i spanska för eleverna som läser årskurs 7–9 och gymnasium.",
    ],
  },
  {
    name: "Farhad",
    role: "Handledare åk 7–9 och gymnasiet, tränare MFF-akademin",
    key: "farhad",
    image: farhad,
    paragraphs: [
      "Utbildad matematik- och NO-lärare för både grundskolan och gymnasiet.",
      "”Under mina dryga {years:farhad} år i dessa skolformer har jag upptäckt att det som varit gemensamt för elevens kunskapsutveckling har varit elevens egen tilltro till sin förmåga att klara av olika uppgifter. Därför försöker jag att jobba mycket med just det hos mina elever. En förutsättning för det är att eleven känner sig trygg i sin skolmiljö.”",
    ],
  },
  {
    name: "Bojan",
    role: "Musiklärare",
    key: "bojan2",
    image: bojan2,
    paragraphs: [
      "Bojan är en engagerad och passionerad musiklärare med en djup och livslång kärlek till musik. Intresset tog form redan vid nio års ålder då pianospel blev en naturlig del av vardagen.",
      "Utbildningen som musiklärare, med specialisering i jazzpiano, kombineras med bred musikalisk kompetens som multiinstrumentalist på piano, gitarr och trummor. Musikundervisning ses som en central del av barns kognitiva, kreativa och psykomotoriska utveckling, och undervisningen sker på engelska och spanska.",
    ],
  },
  {
    name: "Erik",
    role: "Handledare distanselever, matte- och NO-lärare åk 4–6",
    key: "erik",
    image: erik,
    paragraphs: [
      "Jag har arbetat inom skola och undervisning sedan 2017 och har erfarenhet från både internationella och privata skolor i Tyskland och Spanien. Sedan tidigare är jag utbildad biolog med inriktning på djurbeteende och djurvård, och är även utbildad biologilärare för högstadiet.",
      "Min styrka i undervisningen är att skapa en inkluderande lärmiljö där alla elever känner sig delaktiga och sedda. Jag lägger stor vikt vid att motivera och uppmuntra mina elever till att tänka kreativt och utveckla sin nyfikenhet för lärande.",
    ],
  },
];

const leadershipAll = staff.slice(0, 2);

type Person = (typeof staff)[number];

function PersonCard({
  p,
  profiles,
  priority = false,
}: {
  p: Person;
  profiles: StaffProfileMap;
  /** Bara de översta porträtten laddas med hög prioritet – resten lazy. */
  priority?: boolean;
}) {
  const profile = staffProfile(profiles, p.key);
  const facts = profileFacts(profile);
  const name = displayName(profile, p.name);
  const role = displayRole(profile, p.role);
  const paragraphs = bioParagraphs(profile) ?? p.paragraphs;
  return (
    <article className="grid gap-6 rounded-xl border border-border bg-card p-6 shadow-soft sm:grid-cols-[10rem_1fr] sm:p-8">
      <Portrait
        src={p.image}
        personKey={p.key}
        priority={priority}
        eager
        alt={`Porträtt av ${name}, ${role.toLowerCase()}`}
        className="h-40 w-40"
      />


      <div>
        <h3 className="text-xl font-semibold">{name}</h3>
        <p className="mt-1 text-sm text-primary">{role}</p>
        {facts.length > 0 ? (
          <dl className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-2">
            {facts.map((f) => (
              <div key={f.label} className="text-sm">
                <dt className="text-xs font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                  {f.label}
                </dt>
                <dd className="mt-0.5 leading-snug">{f.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/90">
          {paragraphs.map((text, j) => (
            <p key={j}>{fillYearsFromProfiles(text, profiles)}</p>
          ))}
        </div>
      </div>
    </article>
  );
}

function Personal() {
  const portraits = useStaffPortraits();
  const profiles = useStaffProfiles();
  const visible = (p: Person) => !portraits[p.key]?.hidden && !portraits[p.key]?.deletedAt;
  // Gruppen kan skrivas över i admin (Personal → Grupp på personalsidan).
  const groupOf = (p: Person) =>
    displayGroup(
      staffProfile(profiles, p.key),
      leadershipAll.some((l) => l.key === p.key) ? "Skolledning" : "Lärare och medarbetare",
    );
  const leadership = staff.filter((p) => visible(p) && groupOf(p) === "Skolledning");
  const team = staff.filter((p) => visible(p) && groupOf(p) !== "Skolledning");
  return (

    <>
      <PageHero
        eyebrow="Vårt team"
        title="Personal"
        intro="Vår målsättning är att all svensk undervisning ges av lärare med svensk lärarlegitimation. Spanska undervisas av lärare med relevant behörighet. Möt personalen som arbetar på skolan."
      />

      <div className="mx-auto max-w-5xl px-5 py-16">
        <section>
          <h2 className="text-2xl font-semibold sm:text-3xl">Skolledning</h2>
          <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
            Skolledningen ansvarar för det pedagogiska arbetet i vardagen. Skolans grundare och
            styrelse presenteras på sidan Om skolan.
          </p>
          <div className="mt-8 space-y-8">
            {leadership.map((p, i) => (
              <PersonCard key={`${p.name}-${i}`} p={p} profiles={profiles} priority={i < 2} />
            ))}
          </div>
          <p className="mt-6">
            <Link
              to="/om-skolan"
              className="font-semibold text-primary link-quiet"
            >
              Läs om grundare och ledning →
            </Link>
          </p>
        </section>

        <section className="mt-16">
          <h2 className="text-2xl font-semibold sm:text-3xl">Lärare och medarbetare</h2>
          <div className="mt-8 space-y-8">
            {team.map((p, i) => (
              <PersonCard key={`${p.name}-${i}`} p={p} profiles={profiles} priority={i < 2} />
            ))}
          </div>
        </section>
      </div>

      <FaqBlock items={faqFor("personal", "sv")} locale={"sv"} />

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
